/**
 * Historical era scene — the Kaaba ~605 C.E (before Muhammad). Registered under
 * the era id `kaaba-600ce` (matches the backend scene id; i18n labelKey `y600`,
 * title "605").
 * Same shape as the Bronze Age scene (`kaaba2000Bce.ts`): STANDALONE world-scale
 * config, whitelisted for `scene_open` (chat + POI), child of `kaaba` on the
 * stack, entered via the slider's Explore CTA.
 *
 * Splat transform: NONE (identity) — world-scale export.
 *
 * Camera / controls / colliders / reveal are copied from the Bronze Age scene as
 * a starting baseline (same location, identity transform). Re-tune for this
 * model via `?dev` (Colliders, Camera → Copy Pose).
 */
import type { SceneConfig } from '../sceneConfig';
import { resolveCdnUrl } from '../../utils/cdnUrl';

const KAABA_605_2M_URL = resolveCdnUrl('models/historical/kaaba/Kaaba_605_v3_2M.sog');
const KAABA_605_1M_URL = resolveCdnUrl('models/historical/kaaba/Kaaba_605_v4_1M.sog');
const KAABA_605_600K_URL = resolveCdnUrl('models/historical/kaaba/Kaaba_605_v4_600K.sog');

const TARGET_POINT = { x: -0.08, y: 0.24, z: -0.03 };

export const kaaba605SceneConfig: SceneConfig = {
  id: 'kaaba-600ce',
  renderer: {
    clearColor: 0x0f0f13,
  },
  camera: {
    fov: 75,
    near: 0.01,
    far: 1e4,
    initialPosition: { x: -1.52, y: 1.03, z: 0.76 },
    previewTourStart: { x: -5.78, y: 3.17, z: 2.61 },
    previewTourEnd: {
      position: { x: 1.46, y: 0.39, z: -2.43 },
      target: TARGET_POINT,
    },
    flyover: {
      delay: 1,
      segments: [
        { type: 'waypoint', position: { x: 1.46, y: 2.101, z: -2.43 }, target: { x: -0.08, y: 0.24, z: -0.03 }, duration: 1 },
        { type: 'orbit', center: { x: -0.08, y: 0.24, z: -0.03 }, radius: 2.852, height: 1.861, angleDeg: 160, duration: 2 },
        { type: 'waypoint', position: { x: -0.928, y: 1.111, z: 4.222 }, target: { x: -0.08, y: 0.24, z: -0.03 }, duration: 1 },
      ],
    },
  },
  controls: {
    orbitMinDistance: 0,
    orbitMaxDistance: 10000,
    orbitMinPolarAngle: 0,
    // Cap the lowest tilt at polar 87° — camera can't drop below ~horizontal.
    orbitMaxPolarAngle: (87 / 180) * Math.PI,
    constrainVertical: false,
    clampMinHeight: -Infinity,
    clampMaxHeight: Infinity,
    clampMaxHeightFar: Infinity,
    orbitCenter: TARGET_POINT,
  },
  // Baseline from the Bronze Age scene — re-tune for this model via ?dev panels.
  colliders: [
    {
      id: 'kaaba-cube',
      type: 'box',
      mode: 'solid',
      enabled: true,
      position: { x: 0.24406565926671858, y: 0.18040189167878068, z: -0.0650466856549024 },
      size: { x: 1.7, y: 1.2, z: 1 },
      rotation: { x: 0, y: 0.15707963267948954, z: 0 },
    },
    {
      id: 'scene-boundary',
      type: 'cylinder',
      mode: 'hollow',
      enabled: true,
      blocksLineOfSight: false,
      position: { x: -0.08, y: 0.24, z: -0.03 },
      radius: 7,
      height: 7,
    },
  ],
  splat: {
    defaultByQuality: {
      desktop: 'kaaba_605_2m',
      mobile: { high: 'kaaba_605_1m', middle: 'kaaba_605_1m', low: 'kaaba_605_600k' },
    },
    // No transform → identity (world-scale export).
    variants: [
      { key: 'kaaba_605_2m', url: KAABA_605_2M_URL },
      { key: 'kaaba_605_1m', url: KAABA_605_1M_URL },
      { key: 'kaaba_605_600k', url: KAABA_605_600K_URL },
    ],
    revealAnimationDuration: 3,
    revealDistanceScale: 0.8,
    revealMode: '3d',
    revealOrigin: TARGET_POINT,
  },
  previewModel: {
    url: resolveCdnUrl('preview/Kaaba_605_preloader_pnts_150000_meshopt(16).glb'),
    style: {
      pointSizeFactor: 2.5,
      wavesCenter: TARGET_POINT,
    },
  },
  assistant: {
    position: { x: 0, y: 0, z: -0.03 },
    rotationY: 0,
    scale: 0.0000625,
    reflectionResolution: 128,
  },
  environment: {
    hemisphereLight: {
      skyColor: 0xffe6c4,
      groundColor: 0x1a2027,
      intensity: 0.45,
    },
    directionalLights: [
      {
        color: 0xfff2d5,
        intensity: 0.85,
        position: { x: -0.8, y: 1.2, z: 0.6 },
      },
      {
        color: 0xbdd4ff,
        intensity: 0.35,
        position: { x: 0.9, y: 0.6, z: -0.45 },
      },
    ],
    background: {
      color: 0x0f0f13,
      skybox: {
        url: resolveCdnUrl('environment/autumn_field_puresky_8k.jpg'),
        mobileUrl: resolveCdnUrl('environment/autumn_field_puresky_2k.jpg'),
        intensity: 1.0,
        blur: 0.0,
        rotation: [0, 205, 0],
      },
    },
  },
};
